import React, { useState, useMemo } from 'react';
import {
    Box, Grid, Typography, Divider, Paper, Stack,
    TextField, InputAdornment, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import Back from '../../../common/components/BackNavigateComponent';
import { useUserList } from '../../../common/API/hooks';
import UserTable from './UserTable';
import UserDrawer from './UserDrawer';
import Fuse from 'fuse.js';
import { useDebounce } from 'use-debounce';
import { CSVLink } from 'react-csv';

const BRAND = '#0F2FEB';

function StatCard({ icon, label, value, color, bgColor, loading }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${color}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: bgColor, color, display: 'flex' }}>
                    {React.cloneElement(icon, { fontSize: 'medium' })}
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color={color}>
                        {loading ? '—' : value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

export default function UserManagement() {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebounce(searchQuery, 300);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState('add');
    const [selectedUser, setSelectedUser] = useState(null);

    const { data: userList, isLoading } = useUserList();

    const filteredUsers = useMemo(() => {
        if (!userList) return [];
        if (!debouncedSearch) return userList;
        const fuse = new Fuse(userList, {
            keys: ['last_name', 'first_name', 'adid', 'username', 'groups'],
            threshold: 0.3,
        });
        return fuse.search(debouncedSearch).map(r => r.item);
    }, [userList, debouncedSearch]);

    const stats = useMemo(() => {
        if (!userList) return { total: 0, withGroups: 0, withoutGroups: 0 };
        return {
            total: userList.length,
            withGroups: userList.filter(u => u.groups?.length > 0).length,
            withoutGroups: userList.filter(u => !u.groups?.length).length,
        };
    }, [userList]);

    const csvData = useMemo(() => (userList || []).map(u => ({
        ...u,
        groups: u.groups?.join(', ') || '',
    })), [userList]);

    const csvHeaders = [
        { label: 'Last Name', key: 'last_name' },
        { label: 'First Name', key: 'first_name' },
        { label: 'ADID', key: 'adid' },
        { label: 'Email', key: 'email' },
        { label: 'Groups', key: 'groups' },
        { label: 'Date Added', key: 'date_added' },
        { label: 'Last Login', key: 'last_login' },
    ];

    const handleAddUser = () => {
        setSelectedUser(null);
        setDrawerMode('add');
        setDrawerOpen(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setDrawerMode('edit');
        setDrawerOpen(true);
    };

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, mb: 2 }}>
                <Back />
                <Typography variant="h5" fontWeight="bold" sx={{ ml: 1 }}>
                    User Management
                </Typography>
            </Stack>
            <Divider sx={{ borderBottom: 3, mb: 3 }} />

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<PeopleAltRoundedIcon />} label="Total Users" value={stats.total}
                        color={BRAND} bgColor="#EEF1FD" loading={isLoading} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<GroupRoundedIcon />} label="Assigned to Groups" value={stats.withGroups}
                        color="#2E7D32" bgColor="#EDF7EE" loading={isLoading} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<PersonOffRoundedIcon />} label="No Group Assigned" value={stats.withoutGroups}
                        color="#ED6C02" bgColor="#FFF4E5" loading={isLoading} />
                </Grid>
            </Grid>

            {/* Toolbar */}
            <Paper variant="outlined" sx={{
                borderRadius: 2, borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
                p: 1.5, bgcolor: '#F8F9FA',
            }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                    alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <TextField
                        size="small"
                        placeholder="Search by name, ADID, email, or group…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        sx={{ bgcolor: 'white', minWidth: { sm: 340 }, width: { xs: '100%', sm: 'auto' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Stack direction="row" spacing={1} flexShrink={0}>
                        <CSVLink
                            data={csvData}
                            headers={csvHeaders}
                            filename="users_export.csv"
                            style={{ textDecoration: 'none' }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadRoundedIcon />}
                                disabled={!userList?.length}
                                sx={{
                                    textTransform: 'none', color: BRAND, borderColor: BRAND,
                                    height: '40px',
                                }}
                            >
                                Export CSV
                            </Button>
                        </CSVLink>
                        <Button
                            variant="contained"
                            sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                            startIcon={<PersonAddAlt1RoundedIcon />}
                            onClick={handleAddUser}
                        >
                            Add User
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Results count */}
            {!isLoading && userList && (
                <Box sx={{ px: 2, py: 0.75, bgcolor: '#FAFAFA', borderLeft: '2px solid #E0E0E0', borderRight: '2px solid #E0E0E0' }}>
                    <Typography variant="caption" color="text.secondary">
                        {debouncedSearch
                            ? `Showing ${filteredUsers.length} of ${userList.length} users`
                            : `${userList.length} user${userList.length !== 1 ? 's' : ''} total`}
                    </Typography>
                </Box>
            )}

            {/* Table */}
            <Paper variant="outlined" sx={{
                borderRadius: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 0,
            }}>
                <UserTable filteredUsers={filteredUsers} isLoading={isLoading} onEditUser={handleEditUser} />
            </Paper>

            {/* Drawer */}
            <UserDrawer
                isOpen={drawerOpen}
                mode={drawerMode}
                user={selectedUser}
                onClose={() => setDrawerOpen(false)}
            />
        </Box>
    );
}